import { useState } from 'react';
import { fetchApi } from '../../lib/api';

function NDACard({ connection, ndaRecords, matchScore, onRefresh }) {
  const [signing, setSigning] = useState(false);
  const [signatureText, setSignatureText] = useState('');
  const [showSignForm, setShowSignForm] = useState(false);
  const [error, setError] = useState('');

  const inventor = connection.inventor || {};
  const latestNda = ndaRecords?.[0];

  // Determine NDA status
  let ndaStatus = 'unsigned';
  if (latestNda?.company_action_at) {
    ndaStatus = 'fully_executed';
  } else if (latestNda?.inventor_signed_at && latestNda?.countersign_required) {
    ndaStatus = 'awaiting_countersign';
  } else if (latestNda?.inventor_signed_at && !latestNda?.countersign_required) {
    ndaStatus = 'awaiting_acknowledge';
  }

  const handleSign = async (action) => {
    if (!signatureText.trim()) { setError('Please type your full legal name'); return; }
    setSigning(true);
    setError('');
    try {
      await fetchApi(`/api/nda/${action}`, {
        method: 'POST',
        body: JSON.stringify({ nda_record_id: latestNda.id, signature_text: signatureText }),
      });
      setShowSignForm(false);
      setSignatureText('');
      onRefresh();
    } catch (err) {
      setError(err.message);
    }
    setSigning(false);
  };

  const scoreBg = matchScore >= 90 ? 'bg-emerald-100 text-emerald-700' :
                  matchScore >= 75 ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-gray-500';

  return (
    <div className="border-2 border-vault-border rounded-2xl p-5 hover:border-vault-gold/30 transition-colors">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold text-sm">
            {inventor.display_name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <div className="font-semibold text-vault-navy text-sm">{inventor.display_name || 'Inventor'}</div>
            <div className="text-xs text-gray-400">{inventor.email}</div>
          </div>
        </div>
        {matchScore > 0 && (
          <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${scoreBg}`}>
            {matchScore}% match
          </span>
        )}
      </div>

      {ndaStatus === 'unsigned' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700 font-medium">
          NDA Pending — Awaiting Inventor Signature
        </div>
      )}

      {ndaStatus === 'awaiting_countersign' && !showSignForm && (
        <button
          onClick={() => setShowSignForm(true)}
          className="w-full py-3 rounded-btn text-sm font-semibold text-white transition-all"
          style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
        >
          Countersign NDA
        </button>
      )}

      {ndaStatus === 'awaiting_acknowledge' && !showSignForm && (
        <button
          onClick={() => setShowSignForm(true)}
          className="w-full py-3 rounded-btn text-sm font-semibold text-white transition-all"
          style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
        >
          Acknowledge Receipt
        </button>
      )}

      {showSignForm && (ndaStatus === 'awaiting_countersign' || ndaStatus === 'awaiting_acknowledge') && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-gray-500 leading-relaxed">
            {ndaStatus === 'awaiting_countersign'
              ? 'By typing your full legal name below, you are countersigning the NDA provided by your company.'
              : 'By typing your full legal name below, you acknowledge receipt of the signed NDA.'}
          </p>
          {error && <div className="text-xs text-vault-error bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</div>}
          <input
            className="w-full px-4 py-3 rounded-input border-2 border-vault-border text-sm font-body text-vault-navy bg-white/70 focus:border-vault-gold focus:outline-none"
            placeholder="Type your full legal name"
            value={signatureText}
            onChange={e => setSignatureText(e.target.value)}
          />
          <div className="flex gap-2">
            <button
              onClick={() => handleSign(ndaStatus === 'awaiting_countersign' ? 'countersign' : 'acknowledge')}
              disabled={signing}
              className="flex-1 py-2.5 rounded-btn text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
            >
              {signing ? 'Processing...' : ndaStatus === 'awaiting_countersign' ? 'Countersign NDA' : 'Acknowledge Receipt'}
            </button>
            <button
              onClick={() => { setShowSignForm(false); setError(''); }}
              className="px-4 py-2.5 rounded-btn text-sm font-semibold text-gray-400 border-2 border-vault-border"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {ndaStatus === 'fully_executed' && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-2 text-sm text-emerald-700 font-medium">
          <span>✓</span> NDA Complete — Zoom link released
        </div>
      )}
    </div>
  );
}

export default function NDAInbox({ connections, ndaMap, matchScores, onRefresh }) {
  if (!connections?.length) {
    return (
      <div className="glass p-7">
        <h2 className="font-heading text-xl text-vault-navy mb-2">Inventor Pitch Requests</h2>
        <p className="text-sm text-gray-400 mb-5">
          Inventors who want to pitch to you at this event. Review and manage NDAs before their Zoom link is released.
        </p>
        <div className="text-center py-8">
          <p className="text-sm text-gray-400">No inventor pitch requests yet.</p>
          <p className="text-xs text-gray-300 mt-1">
            Once inventors match with you and register for this event, they'll appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="glass p-7">
      <h2 className="font-heading text-xl text-vault-navy mb-2">Inventor Pitch Requests</h2>
      <p className="text-sm text-gray-400 mb-5">
        Inventors who want to pitch to you at this event. Review and manage NDAs before their Zoom link is released.
      </p>
      <div className="space-y-3">
        {connections.map(conn => (
          <NDACard
            key={conn.id}
            connection={conn}
            ndaRecords={ndaMap[conn.id] || []}
            matchScore={matchScores[conn.inventor_id] || 0}
            onRefresh={onRefresh}
          />
        ))}
      </div>
    </div>
  );
}
